import * as React from 'react';
import styled from 'styled-components';
import Text11 from '@sdk/components/ui/typography/Text11';
import isToday from '@sdk/utils/dateUtils/isToday';
import Avatar from '@sdk/components/avatar/Avatar';
import EllipsisStyle from '@sdk/components/ui/EllipsisStyle';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';
import type { LaborUISchemaType } from '@sdk/labor/types';

import type { RowDataType } from '../types/RowType';
import type {
  OpenEventCallback,
  AddEventType,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
  OptionsFilterType,
} from '../types';
import type { FormattedDateString } from '../types/FormattedDateString';
import type { IsEventSelectedType, ToggleEventType } from '../hooks/useEventSelection';
import FlexRow from '../ui/FlexRow';
import FlexColumn from '../ui/FlexColumn';
import Margin from '../ui/Margin';
import EventBox from '../EventBox';
import useHoverColumns from '../hooks/useHoverColumns';
import ShowPlus from '../components/ShowPlus';

import UserCellSummary from './rows/UserCellSummary';
import ScheduleCell from './ScheduleCell';

const BORDER_COLOR = 'rgba(0, 0, 0, 0.08)';

const Row = styled(FlexRow)`
  height: 100%;
  border-top: 1px solid ${BORDER_COLOR};
  background-color: #f3f5f7;
`;

const Column = styled(ScheduleCell)`
  padding-top: 8px;
`;

const FlexMargin = styled(Margin)`
  display: flex;
  flex: 1;
`;

const FlexGrowColumn = styled(FlexColumn)`
  flex: 1;
`;

const Ellipsis = styled.div`
  ${EllipsisStyle};
  max-width: 95%;
`;

type UserNameTextProps = {
  inactive: boolean;
};
const UsernameText = styled(Text11)<UserNameTextProps>`
  text-decoration: ${({ inactive }): string => (inactive ? 'line-through' : 'none')};
`;

type Props = {
  columns: Array<FormattedDateString>;
  rowData: RowDataType;
  openEvent: OpenEventCallback;
  columnsCount?: number;
  shiftAppearanceMode: DisplayLayoutModeType;
  addEvent: AddEventType;
  toggleEvent: ToggleEventType;
  hasSelectedEvents: boolean;
  isEventSelected: IsEventSelectedType;
  isShiftLineVisible: IsShiftLineVisibleType;
  shouldShowPlus: ShouldShowPlusType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
  laborUISchema: LaborUISchemaType;
  optionsFilter: OptionsFilterType;
};
const RegularFirstRow: React.FC<React.PropsWithChildren<Props>> = ({
  columns,
  rowData,
  openEvent,
  columnsCount,
  shiftAppearanceMode,
  addEvent,
  toggleEvent,
  hasSelectedEvents,
  isEventSelected,
  isShiftLineVisible,
  shouldShowPlus,
  isBusinessClosed,
  isHolidayPay,
  laborUISchema,
  optionsFilter,
}) => {
  const {
    hasNameColumn = true,
    user,
    events,
    group,
    isLast,
    eventHeight,
    withBottomSeparator,
  } = rowData;
  const { isHovered, toggleHover } = useHoverColumns(columns);
  const inactive = user?.deleted || !user?.active;

  return (
    <Row>
      {hasNameColumn && Boolean(user) && (
        <Column columnsCount={columnsCount} isLast={isLast} bottomBorder={withBottomSeparator}>
          <FlexRow>
            {hasNameColumn && <Avatar user={user} size={32} inactive={inactive} />}
            <FlexMargin left={6}>
              <FlexGrowColumn>
                <Ellipsis>
                  <UsernameText inactive={inactive} themeColor={ThemeColorType.gray} bold>
                    {user.fullname}
                  </UsernameText>
                  <UserCellSummary
                    user={user}
                    optionsFilter={optionsFilter}
                    laborUISchema={laborUISchema}
                  />
                </Ellipsis>
              </FlexGrowColumn>
            </FlexMargin>
          </FlexRow>
        </Column>
      )}
      {columns.map((day) => {
        const event = events[day];
        const userForEvent = (group as any).isForDtstart ? null : user;
        const showPlus: boolean =
          isHovered(day) && shouldShowPlus({ user: userForEvent, group, day });
        const toggle = (): void => toggleHover(day);

        return (
          <Column
            key={day}
            columnsCount={columnsCount}
            isToday={isToday(day)}
            isBusinessClosed={isBusinessClosed({ day })}
            isHolidayPay={isHolidayPay({ day })}
            isLast={isLast}
            onMouseEnter={toggle}
            onMouseLeave={toggle}
            bottomBorder={withBottomSeparator}>
            {Boolean(event) && (
              <EventBox
                key={event.id}
                event={event}
                openEvent={openEvent}
                shiftAppearanceMode={shiftAppearanceMode}
                hoverComponent={Column}
                toggleEvent={toggleEvent}
                hasSelectedEvents={hasSelectedEvents}
                isEventSelected={isEventSelected}
                isExchange={false}
                isShiftLineVisible={isShiftLineVisible}
                height={eventHeight}
                showPlus={showPlus}
                isHovered={isHovered(day)}
              />
            )}

            {showPlus && (
              <ShowPlus day={day} group={group} addEvent={addEvent} event={event} user={user} />
            )}
          </Column>
        );
      })}
    </Row>
  );
};

export default RegularFirstRow;
